import CandidateComponent from '@/public/vote/components/candidate-component/candidate-component.vue';
import CandidateSearchComponent from '@/public/vote/components/candidate-search-component/candidate-search-component.vue';
import ElectionComponent from '@/public/vote/components/election-types/election-component/election-component.vue';
import MinMaxValidationHintComponent from '@/public/vote/components/min-max-validation-hint-component/min-max-validation-hint-component.vue';
import { ApiClient } from '@/shared/api-client';
import { IComplexId, IVotingSettings } from '@/shared/models';

import { IHttpError, IVote } from '@/shared/api-client/models';
import { ConsoleFacade } from '@/shared/helpers/console-facade';
import _ from 'lodash';
import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator';

import { ICandidate } from '@/shared/api-client/models/candidate';
import { IEncryptedBallotVote } from '@/shared/api-client/models/encrypted-ballot-vote';
import { ILegacyBallotVote } from '@/shared/api-client/models/legacy-ballot-vote';
import { ArgumentValidator, ComplexIdHelper } from '@/shared/helpers';
import { VoteEncryption } from '@/shared/helpers/vote-encryption';
import { IElection } from '@/shared/models/election';
import { ICandidatureElectionVote } from '../../models/candidature-election-vote';
import { IElectionInitializationReceipt } from '../../models/election-initialization-receipt';
import { IVoteValidationError, VoteValidationService } from '../../services/vote-validation-service';
import { VoteEnvironmentStore } from '../../services/vote-environment-store';
import { IPreparedAdditionalData } from '../../models/prepared-additional-data';

interface IViewModel {

    isLoaded: boolean;

    isStoring: boolean;
}

interface IModals {

    confirmVoteModalName: string;

    confirmBlankVoteModalName: string;

    generalErrorModalName: string;

    validationErrorModalName: string;
}

@Component({
    components: {
        CandidateComponent,
        CandidateSearchComponent,
        MinMaxValidationHintComponent,
        ElectionComponent
    }
})
export default class VoteComponent extends Vue {

    @Inject('voteEnvironment') readonly voteEnvironment!: VoteEnvironmentStore;

    @Prop() public election!: IElection;
    @Prop() public votingSettings!: IVotingSettings;
    @Prop({ default: true }) public showProfilePictures!: boolean;
    @Prop() public translations!: { [key: string]: string; };
    @Prop({default: null}) public preparedAdditionalData!: IPreparedAdditionalData[];

    @Emit('voteStored')
    // tslint:disable-next-line:no-empty
    public emitKeyStored(electionId: string): string {

        ArgumentValidator.notNullOrEmpty(electionId, `electionId`);

        return electionId;
    }

    public votes: ICandidatureElectionVote[] = [];

    public viewModel: IViewModel = {
        isLoaded: false,
        isStoring: false
    };

    public modals: IModals = {
        confirmVoteModalName: 'confirmVoteModal',
        confirmBlankVoteModalName: 'confirmBlankVoteModal',
        generalErrorModalName: 'generalErrorModal',
        validationErrorModalName: 'validationErrorModal'
    };

    private voteValidationService: VoteValidationService = new VoteValidationService();

    private validationErrorEnabled = false;

    private electionInitializationReceipts: IElectionInitializationReceipt[] = [];

    private allValidationErrors: IVoteValidationError[] = [];

    public get validationErrors(): IVoteValidationError[] {

        if (!this.validationErrorEnabled) {
            return [];
        }

        return this.allValidationErrors;
    }

    public get hasRankedElections(): boolean {

        if (this.election.electionType === 'Candidacy') {
            return this.election.votingType === 'Ranked';
        }

        if (this.election.electionType === 'Collection') {

            return this.election.elections.some(x => x.votingType === 'Ranked');
        }

        return false;
    }

    public get isVotable(): boolean {

        if (!this.election) {

            return false;
        }

        const votableElectionIds: string[] = [];

        if (this.election.electionType === 'Collection') {

            this.election.elections.forEach(x => {

                votableElectionIds.push(ComplexIdHelper.createId(x.electionId));
            });
        }

        if (this.election.electionType === 'Candidacy') {

            votableElectionIds.push(ComplexIdHelper.createId(this.election.electionId));
        }

        let enableVoting = false;

        votableElectionIds.forEach(electionId => {

            const electionInitializationReceipt = _.find(this.electionInitializationReceipts, x => x.electionId === electionId);

            if (!electionInitializationReceipt) {
                return;
            }

            if (electionInitializationReceipt.nbrOfCandidatures <= 0) {
                return;
            }

            enableVoting = true;

        });

        return enableVoting;
    }

    public get isValid(): boolean {

        if (this.viewModel.isStoring) {

            return false;
        }

        this.allValidationErrors = this.voteValidationService.validate(this.election, this.votes);

        return !this.allValidationErrors || this.allValidationErrors.length === 0;
    }

    private apiClient: ApiClient = new ApiClient();

    public handleVoteChanged(votes: ICandidatureElectionVote): void {

        _.remove(this.votes, (x) => x.electionId.prefix === votes.electionId.prefix && x.electionId.id === votes.electionId.id);

        this.votes.push(votes);
    }

    public handleInitiated(receipt: IElectionInitializationReceipt): void {

        this.electionInitializationReceipts.push(receipt);
    }

    public showVoteModal($event: UIEvent): void {

        this.validationErrorEnabled = true;

        $event.preventDefault();

        if (this.election.isLocked) {

            $(`#${this.modals.generalErrorModalName}`).modal('show');

            return;
        }

        if (!this.isValid) {

            $(`#${this.modals.validationErrorModalName}`).modal('show');

            return;
        }

        $(`#${this.modals.confirmVoteModalName}`).modal('show');
    }

    public showVoteBlankModal($event: UIEvent): void {

        $event.preventDefault();

        if (this.election.isLocked) {

            $(`#${this.modals.generalErrorModalName}`).modal('show');

            return;
        }

        $(`#${this.modals.confirmBlankVoteModalName}`).modal('show');
    }

    public async vote($event: UIEvent) {

        $event.preventDefault();

        this.storeVote(
            this.votes.map(x => {

                return {
                    electionId: this.complexIdToString(x.electionId),
                    candidatesVerificationHash: x.candidatesVerificationHash || '',
                    isBlankVote: x.isBlankVote,
                    candidatureIds: x.candidatures !== undefined ? x.candidatures.map((c: ICandidate) => c.id) : []
                };
            })
        );
    }

    public async voteBlank($event: UIEvent) {

        $event.preventDefault();

        this.storeVote(
            this.votes.map(x => {

                return {
                    electionId: this.complexIdToString(x.electionId),
                    candidatesVerificationHash: x.candidatesVerificationHash || '',
                    isBlankVote: true
                };
            })
        );
    }

    /**
     * Life cycle hook for when component is created
     */
    public async created() {

        this.viewModel.isLoaded = true;
    }

    private async storeVote(votes: IVote[]) {

        if (this.election.ballotBoxEngineType === 'Encrypted') {

            this.storeEncryptedVote(votes);

            return;
        }

        if (this.election.ballotBoxEngineType === 'Legacy') {

            this.storeNotEncryptedVote(votes);

            return;
        }
    }

    private async storeNotEncryptedVote(votes: IVote[]) {

        this.viewModel.isStoring = true;

        const ballotVotes: ILegacyBallotVote[] = [];

        votes.forEach(vote => {
            ballotVotes.push({
                electionId: vote.electionId,
                vote: {
                    candidatesVerificationHash: vote.candidatesVerificationHash,

                    isBlankVote: vote.isBlankVote,

                    candidatureIds: vote.candidatureIds
                }
            });
        });

        ConsoleFacade.log(`Not encrypted votes (count ${ballotVotes.length})`);

        try {
            await this.apiClient.vote.storeNotEncryptedVote(this.election.electionId.id, {
                votes: ballotVotes
            });

            const electionId = this.complexIdToString(this.election.electionId);

            this.emitKeyStored(electionId);

            $(`#${this.modals.confirmVoteModalName}`).modal('hide');

            $(`#${this.modals.confirmBlankVoteModalName}`).modal('hide');

            this.viewModel.isStoring = false;

        } catch (error: any) {

            this.handleVoteError(error as IHttpError);
        }
    }

    private async storeEncryptedVote(votes: IVote[]) {

        this.viewModel.isStoring = true;

        if (!this.election.voteEncryptionPublicKey) {

            throw new Error(`Election votes encryption key is missing`);
        }

        const ballotVotes: IEncryptedBallotVote[] = [];

        votes.forEach(vote => {
            const encryptedVote = new VoteEncryption().encrypt(this.election.voteEncryptionPublicKey as string, vote);

            ballotVotes.push({
                electionId: vote.electionId,
                vote: encryptedVote
            });
        });

        ConsoleFacade.log(`Encrypted votes (count ${ballotVotes.length})`);

        try {
            await this.apiClient.vote.storeEncryptedVote(this.election.electionId.id, {
                votes: ballotVotes
            });

            const electionId = this.complexIdToString(this.election.electionId);

            this.emitKeyStored(electionId);

            $(`#${this.modals.confirmVoteModalName}`).modal('hide');

            $(`#${this.modals.confirmBlankVoteModalName}`).modal('hide');

            this.viewModel.isStoring = false;

        } catch (error: any) {

            this.handleVoteError(error as IHttpError);
        }
    }

    private handleVoteError(error: IHttpError) {

        ArgumentValidator.notNullOrEmpty(error, `error`);

        $(`#${this.modals.confirmVoteModalName}`).modal('hide');

        $(`#${this.modals.confirmBlankVoteModalName}`).modal('hide');

        ConsoleFacade.log('An general error occurred');

        $(`#${this.modals.generalErrorModalName}`).modal('show');

        this.viewModel.isStoring = false;
    }

    private complexIdToString(idObject: IComplexId): string {

        ArgumentValidator.notNullOrEmpty(idObject, `idObject`);

        return `${idObject.prefix}/${idObject.id}`;
    }

}
